import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CashbackStatementResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/breadcrumbs";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function MyCashbackPage() {
  const [data, setData] = useState<CashbackStatementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getMyCashback()
      .then(setData)
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground" data-page="me-cashback" data-state="loading">Loading…</p>;
  if (error || !data) {
    return (
      <div data-page="me-cashback" data-state="error" className="flex flex-col gap-3">
        <h1 className="text-2xl font-semibold">My cashback</h1>
        <p className="text-sm text-destructive">{error ?? "Failed to load"}</p>
      </div>
    );
  }

  const activeLinks = data.links.filter((l) => !l.unlinked_at);

  return (
    <div data-page="me-cashback" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "My profile", to: "/profile" }, { label: "Cashback" }]} />
      <h1 className="text-2xl font-semibold">My cashback</h1>

      <Card>
        <CardHeader>
          <CardTitle>Current balance</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-3xl font-bold" data-field="balance">
            {fmtMoney(data.balance_cents, data.currency)}
          </p>
          <p className="text-xs text-muted-foreground">
            Your share of accrued spend. Reversed or paid-out rows are excluded from the headline.
          </p>

          {activeLinks.length === 0 && (
            <div className="rounded-md border border-dashed p-4 text-sm" data-region="no-links">
              <p className="mb-2">You haven't linked any cashback providers yet.</p>
              <Link to="/shop" className={buttonVariants({ size: "sm" })}>Browse providers</Link>
            </div>
          )}
          {activeLinks.length > 0 && (
            <div className="text-sm" data-region="active-links">
              <p className="mb-2 font-medium">Active links</p>
              <ul className="flex flex-col gap-1">
                {activeLinks.map((l) => (
                  <li key={l.id} className="flex items-center gap-2">
                    <span className="font-mono">•••• {l.external_account_ref_suffix}</span>
                    <span className="text-muted-foreground">linked {fmtDate(l.linked_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {data.accruals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No cashback activity yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="pb-2">Date</th>
                    <th className="pb-2">Provider</th>
                    <th className="pb-2 text-right">Spend</th>
                    <th className="pb-2 text-right">Your share</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.accruals.map((a) => (
                    <tr key={a.id} data-row={a.id}>
                      <td className="py-2">{fmtDate(a.reported_at)}</td>
                      <td className="py-2">{a.provider_name}</td>
                      <td className="py-2 text-right">{fmtMoney(a.spend_cents, a.currency)}</td>
                      <td className="py-2 text-right font-medium">{fmtMoney(a.parent_cents, a.currency)}</td>
                      <td className="py-2">
                        <span
                          data-status={a.status}
                          className={
                            a.status === "accrued"
                              ? "rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900"
                              : a.status === "reversed"
                              ? "rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-900"
                              : "rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-900"
                          }
                        >
                          {a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
